import styled from 'libs/styled';
import useBreakpoint from '@sportson/core-web/hooks/useBreakpoint';
import { useTranslation } from 'react-i18next';

import { type TransformHeadingOutput } from 'libs/wordpress/utils/transformHeading';
import { transformBackgroundImageGroup } from 'libs/wordpress/utils/transformBackground';
import { type AlgoliaArticle } from 'components/Article/types';
import objectKeysToCamelCase from '@grebban/utils/object/keysToCamelCase';

import Wysiwyg from 'libs/wordpress/components/Wysiwyg';
import Text from 'components/Text';
import Button, { type ButtonProps } from 'components/Button';

const ContentWrapper = styled('div')`
    display: flex;
`;

const ImageWrapper = styled('div')`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    border-radius: 4px;
    aspect-ratio: 3/2;
    overflow: hidden;
    margin-bottom: auto;
    position: relative;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const TagWrapper = styled('div')`
    display: flex;
    padding: 6px 8px 4px 8px;
    align-items: flex-start;
    gap: 8px;
    z-index: 1;
    position: absolute;
    top: 12px;
    left: 12px;

    border-radius: var(--corner-radius-round, 4px);
    background: var(--color-transparency-white-85);
    backdrop-filter: blur(6px);
`;

const TextWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
    padding-right: 40px;
`;

export interface PromotionSliderComponentProps extends Record<string, unknown> {
    response: AlgoliaArticle[];
    tag?: string;
    heading?: TransformHeadingOutput;
    button?: ButtonProps;
}

export const HeroComponent = ({ response, customTag, button, heading }) => {
    if (!response || !response?.length) {
        return null;
    }
    const { t } = useTranslation();
    const isMobile = useBreakpoint({ to: 'tablet.sm' });
    const device = isMobile ? 'mobile' : 'desktop';
    const { url, title: articleTitle, hero_content, primary_category: primaryTag, category } = response[0];

    const articleContent = objectKeysToCamelCase(hero_content, {
        recursive: true,
        modifyInputObject: false,
    });

    const altTitle = articleContent?.articleSummary?.alternativeTitle ?? '';
    const backgroundImage = articleContent?.articleImage?.backgroundImage ?? {};

    const cardData = {
        title: heading || altTitle || articleTitle,
        summary: articleContent?.articleSummary?.summary,
        image: {
            src: (transformBackgroundImageGroup(backgroundImage, device).src as string) ?? '',
            alt: (transformBackgroundImageGroup(backgroundImage, device).alt as string) ?? '',
        },
        cardTag: customTag || primaryTag || category[0],
    };

    const { title, summary, image, cardTag } = cardData;
    return (
        <ContentWrapper
            flexDirection={['column', null, null, null, 'row']}
            gap={['24px', null, null, null, '40px']}
            marginBottom={['24px', null, null, null, '40px']}
        >
            <ImageWrapper maxWidth={['100%', null, null, null, '50%']}>
                {cardTag && (
                    <TagWrapper>
                        <Text typography="UI/14_100_0_450_uppercase" color="var(--color-base-black)">
                            {cardTag}
                        </Text>
                    </TagWrapper>
                )}

                {/* TODO: NOTE: Implement proper image component */}
                {image?.src && <img src={image.src} alt={image.alt} />}
            </ImageWrapper>

            <TextWrapper maxWidth={['100%', null, null, null, '50%']}>
                {title && (
                    <Wysiwyg
                        data={title?.text || title}
                        TextComponent={(props) => (
                            <Text
                                as={title?.type || 'h2'}
                                color={title?.color || 'var(--text-color-default)'}
                                typography={['Content/24_130_0_450', null, null, null, 'Content/48_120_0_500']}
                                {...props}
                            />
                        )}
                    />
                )}

                {summary && (
                    <Wysiwyg
                        data={summary}
                        TextComponent={(props) => <Text typography="Content/20_140_0_350" as="p" {...props} />}
                    />
                )}

                {button ? (
                    <Button typography="UI/16_100_0_450" theme={button.theme || 'primary'} to={button.url}>
                        {button.children}
                    </Button>
                ) : (
                    <Button typography="UI/16_100_0_450" theme="primary" to={url}>
                        {t('articles.read_article')}
                    </Button>
                )}
            </TextWrapper>
        </ContentWrapper>
    );
};
